import ConfirmPasswordResetForm from './confirm-password-reset-form'
import React, { useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useRealmApp } from '../../RealmApp'
import isStrongPassword from 'validator/lib/isStrongPassword';
import isLength from 'validator/lib/isLength';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { IoMdClose } from 'react-icons/io';
import { AiOutlineCloseCircle, AiOutlineCheckCircle } from 'react-icons/ai';
import { BiAnalyse } from 'react-icons/bi';
import { useSupabaseApp } from "../../SupabaseApp";

/**
 * ConfirmPasswordResetPage Component
 * 
 * Container component that manages the password reset confirmation flow.
 * Handles password validation, submission to backend, and user feedback.
 * Users access this page via email link with authentication tokens.
 * 
 * Key responsibilities:
 * - Validate password strength and matching
 * - Submit new password to Supabase
 * - Display loading, success, and error states
 * @returns {JSX.Element} Password reset confirmation form with validation
 */

function ConfirmPasswordResetPage() {

    // Get password reset method from Supabase context
    const { completePwdReset } = useSupabaseApp();

    // State to store new and confirm password inputs
    const [pwds, setPwds] = useState({
        newPwd: '',
        confirmPwd: ''
    })

    // State to store alert messages displayed to user
    const [response, setResponse] = useState("");

    /**
     * Handle password reset submission
     * Validates passwords match, submits to Supabase, and displays appropriate feedback
     * @param {Object} passwords - Object with newPwd and confirmPwd properties
     */
    async function handleCompletePwdChange(passwords) {
        // Show loading alert while processing
        setResponse(
            <Alert className="error-message floating-alert" variant="light">
                <div>
                    <div style={{ textAlign: "right" }}><Button className="pitchdeckmessageclose"
                        onClick={(event) => {
                            event.preventDefault()
                            setResponse("")
                        }}>
                        <IoMdClose /></Button></div>
                    <div style={{ display: "flex", paddingTop: "10px" }}>
                        <div style={{ width: "90%" }}>
                            <p><span style={{ color: "#0f9d58" }}><BiAnalyse className="alert-icon" /> Loading</span></p>
                            <p style={{ marginLeft: "28px", fontSize: "14px" }}>Attempting to reset your password</p>
                            <p style={{ marginLeft: "28px", fontSize: "14px" }}>If your waiting time is too long, <a href="https://www.roiquant.com/contact" target="_blank" rel="noopener noreferrer">contact us</a>.</p>
                        </div>
                        <div style={{ width: "10%", textAlign: "right" }}>
                            <Spinner animation="border" variant="secondary" />
                        </div>
                    </div>
                </div>
            </Alert>
        );

        // Verify both passwords match using string comparison
        const passwordsMatch = (passwords.newPwd.localeCompare(passwords.confirmPwd) === 0)
        if (passwordsMatch) {
            // Attempt to update password in Supabase
            try {
                await completePwdReset(passwords.newPwd);

                setResponse(
                    <Alert className="error-message floating-alert" variant="light">
                        <div style={{ textAlign: "right" }}><Button className="pitchdeckmessageclose" onClick={(event) => {
                            event.preventDefault()
                            setResponse("")
                        }}><IoMdClose /></Button></div>
                        <div style={{ paddingTop: "10px" }}>
                            <p style={{ color: "#0f9d58" }}><AiOutlineCheckCircle className="alert-icon" /> Successful</p>
                            <p style={{ marginLeft: "28px" }}>Your password reset was successful! <a href="/login">Click here to log in</a></p>
                        </div>
                    </Alert>
                );
            } catch (error) {
                if (error === "same_password") {
                    setResponse(
                        <Alert className="error-message floating-alert" variant="light">
                            <div style={{ textAlign: "right" }}><Button className="pitchdeckmessageclose" onClick={(event) => {
                                event.preventDefault()
                                setResponse("")
                            }}><IoMdClose /></Button></div>
                            <div style={{ paddingTop: "10px" }}>
                                <p style={{ color: "red" }}><AiOutlineCloseCircle className="alert-icon" /> Error</p>
                                <p style={{ marginLeft: "28px" }}>You have entered an old password that was previously used. Due to our security protocols, please enter a new, unique and strong password to protect your data.</p>
                                <p style={{ marginLeft: "28px" }}>Sorry for the inconvenience, better safe than sorry. If you still have trouble <a href="https://www.roiquant.com/contact" target="_blank" rel="noopener noreferrer">resetting your password, contact us</a>.</p>
                            </div>
                        </Alert>
                    );
                }
                else {

                    setResponse(
                        <Alert className="error-message floating-alert" variant="light">
                            <div style={{ textAlign: "right" }}><Button className="pitchdeckmessageclose" onClick={(event) => {
                                event.preventDefault()
                                setResponse("")
                            }}><IoMdClose /></Button></div>
                            <div style={{ paddingTop: "10px" }}>
                                <p style={{ color: "red" }}><AiOutlineCloseCircle className="alert-icon" /> Error</p>
                                <p style={{ marginLeft: "28px" }}>{error}</p>
                            </div>
                        </Alert>
                    );
                }

            }

        }
        // Passwords don't match - show error
        else {
            setResponse(
                <Alert className="error-message floating-alert" variant="light">
                    <div style={{ textAlign: "right" }}><Button className="pitchdeckmessageclose" onClick={(event) => {
                        event.preventDefault()
                        setResponse("")
                    }}><IoMdClose /></Button></div>
                    <div style={{ paddingTop: "10px" }}>
                        <p style={{ color: "red" }}><AiOutlineCloseCircle className="alert-icon" /> Error</p>
                        <p style={{ marginLeft: "28px" }}>Passwords do not match</p>
                    </div>
                </Alert>
            );

        }
    }

    /**
     * Password validation utility functions
     * Each function returns a boolean indicating if the password meets the requirement
     */

    // Check if new password and confirm password fields match
    function checkPwdMatch(password, confirmPassword) {
        return (password.localeCompare(confirmPassword) === 0)
    }

    // Check if password has minimum 8 characters
    function checkPwdLength(password) {
        return isLength(password, { min: 8 })
    }

    // Get current password length for display
    function measurePwdLength(password) {
        return password.length
    }

    // Check if password contains at least one special symbol
    function checkPwdSymbol(password) {
        return isStrongPassword(password, { minUppercase: 0, minLength: 0, minNumbers: 0, minSymbols: 1, minLowercase: 0 })
    }

    // Check if password contains at least one uppercase letter
    function checkPwdUppercase(password) {
        return isStrongPassword(password, { minUppercase: 1, minLength: 0, minNumbers: 0, minSymbols: 0, minLowercase: 0 })
    }

    // Check if password contains at least one lowercase letter
    function checkPwdLowercase(password) {
        return isStrongPassword(password, { minUppercase: 0, minLength: 0, minNumbers: 0, minSymbols: 0, minLowercase: 1 })
    }

    // Check if password contains at least one number
    function checkPwdNum(password) {
        return isStrongPassword(password, { minUppercase: 0, minLength: 0, minNumbers: 1, minSymbols: 0, minLowercase: 0 })
    }

    return (
        <ConfirmPasswordResetForm handleCompletePwdChange={handleCompletePwdChange} pwds={pwds} setPwds={setPwds} response={response}
            checkPwdMatch={checkPwdMatch} checkPwdLength={checkPwdLength} checkPwdSymbol={checkPwdSymbol} checkPwdUppercase={checkPwdUppercase} checkPwdLowercase={checkPwdLowercase} checkPwdNum={checkPwdNum}
            measurePwdLength={measurePwdLength}
        />
    )
}

export default ConfirmPasswordResetPage;